/**
 * "Added to cart" panel (snippets/wd-cart-added-panel.liquid + sections/wd-cart-added-panel.liquid).
 *
 * - Opened by the PDP add (addToCart() in product-form.js) instead of the cart drawer.
 * - Contents come from the Section Rendering API in product context, so Liquid has the added product and the cart.
 *   Every main line of the product gets a [data-line-key] block; the one of the line that was just added is shown.
 * - Every re-render also refreshes the cart drawer (without opening it) and the cart count, from the same cart state.
 *   The warranty buttons render disabled and are enabled only after that: the shared .add-cart-apple-care handler
 *   reads the main line from the drawer (#CartItem-{index}).
 * - Re-renders after a recommendation quick-add (wd-cart-drawer-recommendations.js) and a warranty add ('wd:care-added').
 */
if (!customElements.get('wd-cart-added-panel')) {
  const CART_ICON_SECTION = 'cart-icon-bubble';

  /** Same main line exclusions as the panel section / wd-cart-items */
  const isMainLine = (item) => {
    const props = item.properties || {};
    return (
      props._isTradeInProduct !== 'true' &&
      props._isBundleProduct !== 'true' &&
      !props._aplcr_main_product_sku &&
      !props._sw_main_product_sku &&
      !props._gwp_main_product_sku
    );
  };

  class WdCartAddedPanel extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.wd-cap__dialog');
      this.content = this.querySelector('.js-cap-content');
      this.liveRegion = this.querySelector('#CartAddedPanel-LiveRegion');

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-cap-view-cart]')) {
          this.viewCart();
        } else if (event.target.closest('[data-cap-close]')) {
          this.close();
        }
      });
      this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());

      this.onCareAdded = this.onCareAdded.bind(this);
      document.addEventListener('wd:care-added', this.onCareAdded);
    }

    disconnectedCallback() {
      document.removeEventListener('wd:care-added', this.onCareAdded);
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    get cartDrawer() {
      return document.querySelector('cart-drawer');
    }

    /**
     * PDP add: items are the cart/add.js response items (main line + anything added with it).
     * Falls back to the cart drawer when the panel can't be rendered (e.g. the pre-release gate).
     */
    async openForAdd({ items = [], variantId, handle }) {
      const mainLine = items.find((item) => String(item.variant_id) === String(variantId) && isMainLine(item));
      this.handle = handle;
      this.variantId = String(variantId);
      this.lineKey = mainLine?.key || null;
      this.setNumber = mainLine?.properties?._set_number || null;
      this.activeElement = document.activeElement;

      const rendered = await this.refresh();
      if (!rendered) {
        this.cartDrawer?.updateItems();
        return;
      }
      this.open();
    }

    open() {
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      // Visible as soon as the class is set (only the transform animates), so focus doesn't wait for transitionend
      requestAnimationFrame(() => {
        const heading = this.querySelector('#CartAddedPanel-Heading');
        if (typeof trapFocus === 'function') trapFocus(this.dialog, heading || this.dialog);
      });
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    }

    /**
     * "View cart (N)": the regular cart drawer, already re-rendered by the last refresh.
     * The page stays dimmed throughout: this overlay is kept until the frame the drawer's backdrop shows (.drawer.active),
     * then dropped without a fade, so the two never stack and the page never undims. The scroll lock passes from
     * close() to the drawer's open() in the same task.
     */
    viewCart() {
      const drawer = this.cartDrawer;
      if (!drawer) {
        this.close();
        return;
      }
      this.classList.add('is-handing-off');
      this.close();
      drawer.open(document.querySelector('#cart-icon-bubble'));

      const handOff = () => {
        if (!drawer.classList.contains('active')) {
          requestAnimationFrame(handOff);
          return;
        }
        this.classList.add('is-instant');
        this.classList.remove('is-handing-off');
        requestAnimationFrame(() => this.classList.remove('is-instant'));
      };
      requestAnimationFrame(handOff);
    }

    async onCareAdded(detail = {}) {
      if (!this.isOpen) return;
      if (detail.setNumber) this.setNumber = detail.setNumber;
      const rendered = await this.refresh({ careAdded: true });
      if (!rendered) return;
      if (this.liveRegion) this.liveRegion.textContent = this.dataset.warrantyAddedMessage;
      // The clicked button was replaced - keep focus inside the panel
      const heading = this.querySelector('#CartAddedPanel-Heading');
      if (heading && typeof trapFocus === 'function') trapFocus(this.dialog, heading);
    }

    /** Re-renders the panel, the cart drawer (closed) and the cart count from the same cart state. */
    async refresh({ careAdded = false } = {}) {
      const url = `${window.Shopify.routes.root}products/${encodeURIComponent(this.handle)}?section_id=${this.dataset.sectionId}&variant=${this.variantId}`;
      try {
        const [html] = await Promise.all([
          fetch(url).then((response) => (response.ok ? response.text() : '')),
          this.cartDrawer?.updateItems({ open: false }),
          this.renderCartIcon(),
        ]);
        const inner = new DOMParser().parseFromString(html, 'text/html').querySelector('.wd-cap__inner');
        if (!inner) return false;

        this.content.replaceChildren(inner);
        this.showLine(careAdded);
        // The drawer now matches the cart state the panel was rendered from
        this.querySelectorAll('.wd-cart-warranty__add').forEach((button) => {
          button.disabled = false;
        });
        if (typeof window.refreshModals === 'function') window.refreshModals();
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    }

    /**
     * Shows the block of the added line. Its key changes when the warranty handler rewrites the line, so it falls back
     * to the line's set number (the handler reports it), then to a line of the variant that just got a warranty.
     */
    showLine(careAdded) {
      const lines = [...this.querySelectorAll('.wd-cap__line')];
      const line =
        lines.find((el) => el.dataset.lineKey === this.lineKey) ||
        (this.setNumber && lines.find((el) => el.dataset.setNumber === this.setNumber)) ||
        (careAdded && lines.find((el) => el.dataset.variantId === this.variantId && el.dataset.hasWarranty === 'true')) ||
        lines.find((el) => el.dataset.variantId === this.variantId) ||
        lines[0];
      if (!line) return;
      line.hidden = false;
      this.lineKey = line.dataset.lineKey;
      this.setNumber = line.dataset.setNumber || this.setNumber;
    }

    async renderCartIcon() {
      const response = await fetch(`${window.Shopify.routes.root}?sections=${CART_ICON_SECTION}`);
      const sections = await response.json();
      const target =
        document.getElementById(CART_ICON_SECTION)?.querySelector('.shopify-section') ||
        document.getElementById(CART_ICON_SECTION);
      const source = new DOMParser()
        .parseFromString(sections[CART_ICON_SECTION] || '', 'text/html')
        .querySelector('.shopify-section');
      if (target && source) target.innerHTML = source.innerHTML;
    }
  }

  customElements.define('wd-cart-added-panel', WdCartAddedPanel);
}
